import { CopyButton } from "@components/shared/CopyButton";
import { MARK_BUYER_ACCEPTED, MARK_DRAFT_SENT } from "@lib/graphql/outreach";
import { Linkedin, Mail, MessageCircle, MessageSquare } from "lucide-react";
import { useState } from "react";
import { useMutation } from "urql";
import { BuyerCard } from "./BuyerCard";
import { ReplyModal } from "./ReplyModal";
import { SnoozePopover } from "./SnoozePopover";
import type { InboxBucket, InboxItem } from "./types";

interface Props {
  item: InboxItem;
  bucket: InboxBucket;
  onChanged: () => void;
  onOpen: (item: InboxItem) => void;
}

const CHANNEL_ICON: Record<string, React.ComponentType<{ size?: number }>> = {
  linkedin_connect: Linkedin,
  linkedin_message: MessageSquare,
  email: Mail,
};

export function InboxRow({ item, bucket, onChanged, onOpen }: Props) {
  const [, markSent] = useMutation(MARK_DRAFT_SENT);
  const [, markAccepted] = useMutation(MARK_BUYER_ACCEPTED);
  const [busy, setBusy] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);

  const draft = item.draft;
  const programmeName = draft?.outreachProgramme.name ?? item.programme?.name ?? null;
  const Icon = draft ? (CHANNEL_ICON[draft.channel] ?? MessageSquare) : MessageCircle;
  const lastSent = item.buyer.lastSentDraftAt;

  const handleMarkSent = async () => {
    if (!draft) return;
    setBusy(true);
    await markSent({ input: { id: draft.id } });
    setBusy(false);
    onChanged();
  };

  const handleMarkAccepted = async () => {
    setBusy(true);
    await markAccepted({ input: { id: item.buyer.id } });
    setBusy(false);
    onChanged();
  };

  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="flex items-start justify-between gap-3">
        <button
          onClick={() => onOpen(item)}
          className="min-w-0 flex-1 text-left"
          aria-label="Open buyer details"
        >
          <BuyerCard buyer={item.buyer} leading={<Icon size={14} />} />
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-text-muted">
            {programmeName && (
              <span className="rounded-full bg-card-alt px-2 py-0.5">{programmeName}</span>
            )}
            {lastSent && <span>sent {daysAgo(lastSent)}</span>}
            {item.buyer.snoozedUntil && (
              <span>wakes {new Date(item.buyer.snoozedUntil).toLocaleDateString()}</span>
            )}
          </div>
        </button>

        {item.buyer.linkedinUrl && (
          <a
            href={item.buyer.linkedinUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-input-border bg-card px-2 py-1 text-xs text-text-secondary hover:bg-card-hover"
          >
            <Linkedin size={12} />
            LinkedIn
          </a>
        )}
      </div>

      {draft && (
        <pre className="mt-3 max-h-32 overflow-y-auto whitespace-pre-wrap rounded bg-card-alt p-3 text-sm text-text-primary">
          {draft.body}
        </pre>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {bucket === "READY_TO_SEND" && draft && (
          <>
            <CopyButton
              getContent={() => ({ html: draft.body.replace(/\n/g, "<br>"), text: draft.body })}
              ariaLabel="Copy draft body"
              title="Copy body to clipboard"
              className="flex items-center gap-1 rounded-md border border-input-border bg-card px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-card-hover"
              size={12}
              withLabel
            />
            <button
              onClick={handleMarkSent}
              disabled={busy}
              className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              Mark sent
            </button>
          </>
        )}
        {bucket === "AWAITING_ACCEPT" && (
          <>
            <button
              onClick={handleMarkAccepted}
              disabled={busy}
              className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              Mark accepted
            </button>
            <button
              onClick={() => setReplyOpen(true)}
              className="rounded-md border border-input-border px-3 py-1.5 text-xs text-text-secondary hover:bg-card-hover"
            >
              Mark replied
            </button>
            <SnoozePopover buyerId={item.buyer.id} onDone={onChanged} />
          </>
        )}
        {bucket === "ACCEPTED_NEEDS_FIRST_MESSAGE" && (
          <>
            <button
              onClick={() => setReplyOpen(true)}
              className="rounded-md border border-input-border px-3 py-1.5 text-xs text-text-secondary hover:bg-card-hover"
            >
              Mark replied
            </button>
            <SnoozePopover buyerId={item.buyer.id} onDone={onChanged} />
          </>
        )}
        {bucket === "REPLIED_NEEDS_FOLLOWUP" && (
          <SnoozePopover buyerId={item.buyer.id} onDone={onChanged} />
        )}
      </div>

      <ReplyModal
        open={replyOpen}
        buyerId={item.buyer.id}
        onClose={() => setReplyOpen(false)}
        onLogged={onChanged}
      />
    </div>
  );
}

function daysAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}
